import * as Sentry from "@sentry/browser";
import { format, isValid, parse } from "date-fns";

import { BankFieldSetLabel } from "client/domain/FormBuilder/BankInformation/BankConnector";
import { isOnOnboardingRoute } from "client/domain/Onboarding/Faqs/Faqs";
import {
  AllMerchantFieldMapper,
  Field,
  FieldSets,
  Merchant,
  SubIndustry,
} from "client/lib/apt-merchant";
import * as FormService from "client/services/form";
import { BankVerificationSource } from "client/services/form";
import * as UserService from "client/services/user";
import { StoreLocationsFile } from "client/types";
import { getClientId } from "client/utils/session";

import { convertSubIndustryFieldForSave } from "./transforms";
import { getAllUtmParams } from "./utm";

const fvmToSave = (
  fields: Field<any>[]
): AllMerchantFieldMapper<{ value?: any }> =>
  fields.reduce(
    (prev, field) => ({
      ...prev,
      [field.propertyName]: field.value ?? "",
    }),
    {}
  );

export const transformForSave = (
  merchant: Merchant,
  storeLocationsFile?: StoreLocationsFile
): { data: any } => {
  const data: any = {
    ...fvmToSave(merchant.lead.fields),
    ...fvmToSave(merchant.companyInformation.fields),
    ...fvmToSave(merchant.customerSupport.fields),
    ...fvmToSave(merchant.primaryBank.fields),
    ...fvmToSave(merchant.secondaryBank.fields),
    ...fvmToSave(merchant.storeDetails.fields),
    owners: merchant.owners.map((o) => fvmToSave(o.fields)),
  };
  /**
   * Putting this here for now. Once we / seema figures out how we're going to persist this,
   * will update to save appropriately
   */
  if (storeLocationsFile?.path) {
    data.storeLocationsFile = storeLocationsFile;
  } else {
    data.locations = merchant.locations.map((l) => fvmToSave(l.fields));
  }

  return {
    data,
  };
};

export const transformForUpdateStoreInfo = (
  merchant: Merchant,
  storeLocationsFile?: StoreLocationsFile
): { data: UserService.UpdateStoreInfoRequest } => {
  const companyInfoFields = merchant.companyInformation.toFieldValueMap(true);
  const storeDetailsFields = merchant.storeDetails.toFieldValueMap(true);
  if (storeDetailsFields.subIndustry) {
    const value = storeDetailsFields.subIndustry;
    storeDetailsFields.subIndustry = (convertSubIndustryFieldForSave(
      String(value) ?? ""
    ) ?? value) as SubIndustry;
  }
  if (storeDetailsFields.numOfStores) {
    storeDetailsFields.numOfStores = Number(storeDetailsFields.numOfStores);
  }
  if (storeDetailsFields.totalAnnualSalesInStore) {
    storeDetailsFields.totalAnnualSalesInStore = Number(
      storeDetailsFields.totalAnnualSalesInStore
    );
  }

  const owners = merchant.owners.map((o) => o.getApplicableValue());
  owners.forEach((o) => {
    if (o.dob) {
      const monthFirst = ["United States", "Canada"].includes(
        merchant.companyInformation.registeredCountry.value ?? ""
      );

      let dateToFormat: Date | undefined;
      if (monthFirst) {
        dateToFormat = parse(o.dob, "MM-dd-yyyy", new Date());
      } else {
        dateToFormat = parse(o.dob, "dd-MM-yyyy", new Date());
      }

      if (isValid(dateToFormat)) {
        o.dob = format(dateToFormat, "yyyy-MM-dd");
      }
    }
  });

  return {
    data: {
      locations: merchant.locations.map((l) => l.toFieldValueMap()),
      owners,
      ...(companyInfoFields as FieldSets.CompanyInformationFieldSetValueMap),
      ...(storeDetailsFields as FieldSets.StoreDetailsFieldSetValueMap),
      storeLocationsFile: storeLocationsFile?.path ?? "",
    },
  };
};

export const transformForSignup = (email: string, merchant: Merchant): any => {
  const modelData = merchant.lead.toFieldValueMap();
  modelData.numOfStores = Number(modelData.numOfStores ?? 0);
  if (modelData.totalAnnualSalesInStore) {
    modelData.totalAnnualSalesInStore = Number(
      modelData.totalAnnualSalesInStore ?? 0
    );
  }

  if (!email) {
    Sentry.captureException(
      new Error(
        `Lead Form is submitted without email for clientId ${getClientId()}.`
      )
    );
  }

  // TODO: type this? it's weird.
  // It's LeadFieldSetValueMap s & { email: string }
  // but for some reason I can't import LeadFieldSetValueMap here
  return {
    ...modelData,
    email,
    ...getAllUtmParams(),
  };
};

export const transformForBankInfo = (
  merchant: Merchant,
  bankLabel: BankFieldSetLabel,
  setBankFormComplete: boolean,
  bankVerificationSource?: BankVerificationSource
): { data: FormService.SubmitBankInfoRequest } => {
  const primaryBankFields = merchant.primaryBank.toFieldValueMap(true);
  const secondaryBankFields = merchant.secondaryBank.toFieldValueMap(true);

  const {
    canadaRoutingInstitutionIdNumber,
    canadaRoutingTransitIdNumber,
    ...restPrimaryBankFields
  } = primaryBankFields;
  let canadaRoutingNumber: string | undefined;
  if (canadaRoutingTransitIdNumber && canadaRoutingInstitutionIdNumber) {
    canadaRoutingNumber = `${canadaRoutingTransitIdNumber}${canadaRoutingInstitutionIdNumber}`;
  }

  const data: FormService.SubmitBankInfoRequest = {
    ...restPrimaryBankFields,
    canadaRoutingNumber,
    bankLabel,
    setBankFormComplete,
  };

  if (typeof bankVerificationSource !== "undefined") {
    data.bankVerificationSource = bankVerificationSource;
  }

  // Handle secondary bank data
  if (bankLabel === "SECONDARY") {
    const {
      account2,
      bankAccountName2,
      bankBranch2,
      bankName2,
      bsb2,
      confirmAccount2,
      directDebitAgreement2,
      email2,
      routing2,
      signatoryName2,
      signatoryType2,
      sortCode2,
    } = secondaryBankFields;

    data.account = account2;
    data.bankAccountName = bankAccountName2;
    data.bankBranch = bankBranch2;
    data.bankName = bankName2;
    data.bsb = bsb2;
    data.confirmAccount = confirmAccount2;
    data.directDebitAgreement = directDebitAgreement2;
    data.email = email2;
    data.routing = routing2;
    data.signatoryName = signatoryName2;
    data.signatoryType = signatoryType2;
    data.sortCode = sortCode2;
  }

  return {
    data,
  };
};

export const transformForCustomerSupportInfo = (
  merchant: Merchant
): { data: UserService.SubmitCustomerSupportInfoRequest } => ({
  data: merchant.customerSupport.toFieldValueMap(true),
});

export const transformForCashPayInfo = (
  merchant: Merchant
): { data: UserService.SubmitCashPayInfoRequest } => ({
  data: merchant.cashPay.toFieldValueMap(true),
});

export const transformForContactUsInfo = (
  merchant: Merchant,
  email: string,
  contentBody: string,
  file?: File
): UserService.CompleteContactUsFormParams => ({
  contentBody,
  file,
  email,
  country: merchant.lead.registeredCountry.value,
  storeName: merchant.lead.storeName.value,
  currentPage: isOnOnboardingRoute(window?.location.pathname)
    ? "Onboarding Page"
    : "Status Page", // opps needs to know which page they send the contact form from. FAQ is only showed on either onboarding or status pages
});
